import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AnnouncementService } from '../../services/announcement.service';
import { AuthService } from '../../services/auth.service';

/**
 * Portal-wide service announcement dialog (mounted in the app shell, like the assistant chat).
 *
 * Acceptance criteria (Casey, 2026-08-19):
 *  1. Centered modal over a dimmed background, appearing on successful login.
 *  2. Fixed copy. Story 919 (Eva Sullivan, 2026-10-06) replaced story 483's sunset wording: SERVICE
 *     ANNOUNCEMENT / All Time Tracking Now in the Portal / the "going forward" paragraph / the
 *     day-to-day + FRESHWORKS paragraph (the Freshworks link is unchanged) / the acknowledging fine
 *     print. No product name, no date, and no personal contact line: Freshworks is the way to get help.
 *     The same copy shows on the tech portal (Conexa-Portal story 919).
 *  3. Exactly two buttons resolve it - "Remind me later" on the LEFT (the announcement returns on a
 *     later sign-in, but at most ONCE EVERY 24 HOURS - per Casey 2026-08-19, supersedes the
 *     criteria's once-per-calendar-day), "Acknowledged - do not show again" on the RIGHT (recorded
 *     SERVER-SIDE per user in tblServiceAnnouncementAck - who + when, sticks across devices).
 *     NO close/X button; clicking outside or pressing Escape does NOT dismiss it.
 *  4. Display lifecycle: DISPLAY_START (null = show now; set it when the release date is defined) and
 *     the KILL DATE - after November 1, 2026 it never shows for anyone, acknowledged or not.
 *  5. The tech Portal shows the same message (Conexa-Portal, story 919, which revived story 503).
 *
 * The 24-hour reminder stamp is localStorage per browser per user (a different device may show it
 * once more within the window - acceptable; the ACK is the server-side record). To publish the next
 * announcement: add its key to the backend AnnouncementKeys catalog and update the key + wording here.
 *
 * Styling is EXPLICIT-COLOR (the reconnecting-page precedent) so the card reads identically on all
 * 10 themes; only the header band rides the theme's --app-bar-grad (with a brand-teal fallback).
 */
@Component({
    selector: 'app-service-announcement',
    template: `
        <div *ngIf="visible" class="sa-overlay" role="presentation">
            <div class="sa-card" role="dialog" aria-modal="true" aria-labelledby="sa-title">
                <div class="sa-header">
                    <span class="sa-icon material-symbols-outlined" aria-hidden="true">notification_important</span>
                    <div>
                        <div class="sa-kicker">Service Announcement</div>
                        <h2 id="sa-title" class="sa-title">All Time Tracking Now in the Portal</h2>
                    </div>
                </div>
                <div class="sa-body">
                    <p>
                        Going forward, all time tracking must be done in the Conexa Portal. This includes
                        timesheets, billing and payroll.
                    </p>
                    <p>
                        Please make sure your day-to-day work is running through the portal. If you have
                        questions or need assistance, submit a request through
                        <a class="sa-link" href="https://cscontractsolutions.freshservice.com/support/home"
                           target="_blank" rel="noopener">Freshworks</a>.
                    </p>
                    <p class="sa-fine">
                        Acknowledging confirms you have read this notice and stops it from appearing again.
                        Otherwise it will appear again the next time you sign in on a new day.
                    </p>
                    <p class="sa-error" *ngIf="errorMessage">{{ errorMessage }}</p>
                </div>
                <div class="sa-actions">
                    <button type="button" class="sa-btn sa-btn-later" (click)="remindLater()" [disabled]="saving">
                        Remind me later
                    </button>
                    <button type="button" class="sa-btn sa-btn-ack" (click)="acknowledge()" [disabled]="saving">
                        {{ saving ? 'Saving...' : 'Acknowledged - do not show again' }}
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .sa-overlay {
            position: fixed;
            inset: 0;
            z-index: 3000;
            background: rgba(10, 22, 28, 0.55);
            backdrop-filter: blur(2px);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
        }
        .sa-card {
            background: #ffffff;
            color: #1f2a30;
            border-radius: 16px;
            box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
            max-width: 560px;
            width: 100%;
            overflow: hidden;
            animation: sa-rise 240ms ease-out;
        }
        @keyframes sa-rise {
            from { opacity: 0; transform: translateY(14px) scale(0.98); }
            to   { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
            .sa-card { animation: none; }
        }
        .sa-header {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 18px 22px;
            background: var(--app-bar-grad, linear-gradient(135deg, #0D3642, #0a4a52 55%, #165963));
            color: #ffffff;
        }
        .sa-icon {
            font-size: 30px;
            width: 46px;
            height: 46px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 14px;
            background: rgba(255, 255, 255, 0.14);
            flex: 0 0 auto;
        }
        .sa-kicker {
            font-size: 11.5px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            opacity: 0.85;
        }
        .sa-title {
            margin: 2px 0 0;
            font-size: 20px;
            font-weight: 700;
            color: #ffffff;
            line-height: 1.2;
        }
        .sa-body {
            padding: 18px 22px 4px;
            font-size: 14.5px;
            line-height: 1.55;
        }
        .sa-body p { margin: 0 0 12px; color: #1f2a30; }
        .sa-link { color: #0a4a52; text-decoration: underline; font-weight: 600; }
        .sa-fine { font-size: 12.5px; color: #5b6b73; }
        .sa-error { color: #b3261e; font-size: 13px; font-weight: 600; }
        .sa-actions {
            display: flex;
            justify-content: space-between;
            gap: 10px;
            padding: 10px 22px 20px;
            flex-wrap: wrap;
        }
        .sa-btn {
            border-radius: 10px;
            padding: 10px 18px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            border: none;
        }
        .sa-btn:disabled { opacity: 0.6; cursor: default; }
        .sa-btn-later {
            background: #ffffff;
            color: #33454e;
            border: 1px solid #cfd9de;
        }
        .sa-btn-later:hover:not(:disabled) { background: #f2f6f7; }
        .sa-btn-ack {
            background: linear-gradient(135deg, #0a4a52, #165963);
            color: #ffffff;
        }
        .sa-btn-ack:hover:not(:disabled) { filter: brightness(1.08); }
        @media (max-width: 700px) {
            .sa-actions { flex-direction: column-reverse; }
            .sa-btn { width: 100%; }
        }
    `],
    standalone: true,
    imports: [CommonModule]
})
export class ServiceAnnouncementComponent implements OnInit, OnDestroy {

    /** Must match the backend AnnouncementKeys catalog exactly. */
    public static readonly ANNOUNCEMENT_KEY = 'portal-timekeeping-2026-10';

    /**
     * Display lifecycle. START null = show as soon as this ships (set it when the release date is
     * defined). KILL: per the acceptance criteria the popup never shows past November 1, 2026 -
     * END is the first LOCAL instant it stops showing (start of November 2, so all of Nov 1 shows).
     */
    public static readonly DISPLAY_START: Date | null = null;
    public static readonly DISPLAY_END: Date = new Date(2026, 10, 2); // months are 0-based: 10 = November

    public visible = false;
    public saving = false;
    public errorMessage = '';

    /** The status check runs at most once per app load - the popup is per sign-in, not per navigation. */
    private checkedThisLoad = false;
    private loginSub?: Subscription;

    constructor(private announcementService: AnnouncementService,
        private authService: AuthService) { }

    public ngOnInit(): void {
        // The shell mounts this behind (isUserLoggedIn | async), but the broadcaster's initial value
        // can be optimistic during bootstrap - so re-check the real state and watch for the JWT-mode
        // late login too.
        this.loginSub = this.authService.isUserLoggedIn.subscribe(loggedIn => {
            if (loggedIn && this.authService.isLoggedIn()) {
                this.checkStatusOnce();
            }
        });
    }

    public ngOnDestroy(): void {
        this.loginSub?.unsubscribe();
    }

    /** True while 'now' is inside the announcement's display window (start/kill dates, local time). */
    public static isWithinDisplayWindow(now: Date): boolean {
        if (ServiceAnnouncementComponent.DISPLAY_START && now < ServiceAnnouncementComponent.DISPLAY_START) {
            return false;
        }
        return now < ServiceAnnouncementComponent.DISPLAY_END;
    }

    /** How long "Remind me later" suppresses the popup: it shows at most once every 24 HOURS, not on every login. */
    public static readonly REMIND_SUPPRESS_MS = 24 * 60 * 60 * 1000;

    /** True when "Remind me later" was clicked within the last 24 hours (this browser, this user). */
    public hasRemindedRecently(now: Date = new Date()): boolean {
        try {
            const stamp = Number(localStorage.getItem(this.remindStorageKey()));
            // NaN (no stamp, or a legacy day-stamp value) reads as not reminded - the popup shows.
            return Number.isFinite(stamp) && now.getTime() - stamp < ServiceAnnouncementComponent.REMIND_SUPPRESS_MS;
        } catch {
            return false; // storage unavailable (private mode) - the popup just shows again
        }
    }

    private checkStatusOnce(): void {
        if (this.checkedThisLoad) return;
        this.checkedThisLoad = true;

        // Lifecycle gates first: outside the display window (or reminded within the last 24 hours)
        // = no popup, no API call needed.
        if (!ServiceAnnouncementComponent.isWithinDisplayWindow(new Date())) return;
        if (this.hasRemindedRecently()) return;

        this.announcementService.getAckStatus(ServiceAnnouncementComponent.ANNOUNCEMENT_KEY).subscribe({
            next: status => this.visible = !status?.acknowledged,
            error: () => { /* best-effort: never block the app over the banner; it tries again next load */ }
        });
    }

    /**
     * Close for now. The announcement returns on a later sign-in, but at most once every 24 hours
     * (a per-user, per-browser timestamp - the server-side ack is the durable opt-out).
     */
    public remindLater(): void {
        if (this.saving) return;
        try {
            localStorage.setItem(this.remindStorageKey(), String(Date.now()));
        } catch { /* storage unavailable - it may show again on the next sign-in */ }
        this.visible = false;
    }

    /** Record the acknowledgment server-side, then dismiss for good. */
    public acknowledge(): void {
        if (this.saving) return;
        this.saving = true;
        this.errorMessage = '';
        this.announcementService.acknowledge(ServiceAnnouncementComponent.ANNOUNCEMENT_KEY).subscribe({
            next: () => {
                this.saving = false;
                this.visible = false;
            },
            error: () => {
                this.saving = false;
                this.errorMessage = 'Your acknowledgment could not be saved. Please try again.';
            }
        });
    }

    /** Reminder stamp key, scoped per user so shared computers do not suppress each other's popup. */
    private remindStorageKey(): string {
        const userId = this.authService.getTokenDetails()?.id ?? 'anon';
        return `sa-remind:${ServiceAnnouncementComponent.ANNOUNCEMENT_KEY}:${userId}`;
    }
}
